import { Plain } from "./Assertions.ts";

// Standard HTTP headers.
const X_FORWARDED_HOST = 'x-forwarded-host'
const X_FORWARDED_PROTO = 'x-forwarded-proto'

// Non-standard HTTP header used by tabserver.
const X_FORWARDED_PATHNAME = 'x-forwarded-pathname'

/**
 * Header carrying an opaque, caller-chosen continuation token that lets a callee
 * group repeated cross-party gateway calls into one continuing downstream
 * session. Carries no identity; it is only ever a sub-key under the caller's
 * verified token (see `expertReuseKey`). Generic on purpose so non-chat callers
 * can reuse it. Copied verbatim through same-daemon forwards by `Forward`, and
 * re-applied by `McpGateway` on the cross-daemon path so the callee can read it.
 */
export const CONTINUATION_HEADER = 'x-tabserver-continuation'

/**
 * Returns the request protocol. This is the first of the following:
 *
 * 1. The `x-forwarded-proto` header, if present, with trailing colon.
 * 2. The request URL protocol, ditto.
 *
 * @param request the request.
 * @returns {string} the protocol with trailing colon.
 */
export function getClientProtocol(request: Request): string {
  if (request.headers.has(X_FORWARDED_PROTO)) {
    return (request.headers.get(X_FORWARDED_PROTO) as string) + ':'
  }

  return new URL(request.url).protocol
}

/**
 * Returns the request host name. This is the first of the following:
 *
 * 1. The `x-forwarded-host` header, if present.
 * 2. The request URL hostname (including port, if present)
 *
 * @param request the request.
 * @returns the protocol.
 */
export function getClientHost(request: Request): string {
  if (request.headers.has(X_FORWARDED_HOST)) {
    return request.headers.get(X_FORWARDED_HOST) || ''
  }

  return new URL(request.url).host
}

/**
 * Returns the request pathname. This is the first of the following:
 *
 * 1. The `x-forwarded-pathname` header, if present.
 * 2. The request URL pathname.
 */
export function getClientPathname(request: Request): string {
  if (request.headers.has(X_FORWARDED_PATHNAME)) {
    return request.headers.get(X_FORWARDED_PATHNAME) || ''
  }

  return new URL(request.url).pathname
}

/**
 * Returns the client request protocol, host and pathname components
 * only.
 *
 * @param request the request.
 * @return {URL} the protocol://host/pathname
 */
export function getClientUrlPhp(request: Request): URL {
  const protocol = getClientProtocol(request)
  const host = getClientHost(request)
  const pathname = getClientPathname(request)
  return new URL(`${protocol}//${host}${pathname}`)
}

/**
 * Returns the URL as requested by the client taking account
 * of the x-forwarded protocol and host.
 *
 * @param request the request.
 * @returns the URL.
 */
export function getClientUrl(request: Request): URL {
  const originalUrl = new URL(request.url)
  const newUrl = getClientUrlPhp(request)
  newUrl.search = originalUrl.search
  newUrl.hash = originalUrl.hash
  return newUrl
}

/**
 * Returns the pathname portion of the request URL, excluding
 * query and fragment components.
 *
 * @param {request} the request.
 * @returns {string} the pathname which starts with a forward slash.
 */
export function getPathname(request: Request): string {
  const url = new URL(request.url)
  return url.pathname
}

/**
 * Returns the string value of a named cookie, or null if it is not
 * present in the request.
 *
 * @param {Request} request the request.
 * @param {string} cookieName the name of the cookie
 * @returns {string} the cookie value, or null if not present.
 */
export function getCookie(request: Request, cookieName: string): string | null {
  const cookies = getCookies(request)
  if (cookieName in cookies) {
    return cookies[cookieName]
  }
  return null
}

/**
 * Returns a map of request cookie names to values, which can be empty.
 *
 * @param {Request} request the request.
 * @returns {Plain} the plain object with zero or more cookie keys.
 */
export function getCookies(request: Request): Plain {
  const cookies: Plain = {}
  const headerValue = request.headers.get('Cookie')
  if (headerValue) {
    for (const keyVal of headerValue.split('; ')) {
      const [key, value] = keyVal.split('=')
      cookies[key] = value
    }
  }
  return cookies
}
